// 261005 E3-P4b1 — MỌI quyết định hiển thị của thẻ khối chặng ① Nguồn (hàm thuần, có jest). KHÔNG import antd/react.
// Mỗi khối = MỘT lệnh (tải lại / tách / gộp chương kế / xoá). Không có "Áp lại" (Leader chốt D2a): sau "Khôi phục bản trước" chỉ có "Xem thử lại".
import type { AssistantBlock, SourceBlockDiff, SourceTrimDiff } from '../../../../types/adaptationAssistant';
import { impactLines, reloadWarningText } from '../../sourceChapterUtils';
import { friendlyJobNote } from '../jobErrorText';

const str = (v: unknown): string => String(v ?? '').trim();
const UNDO_EPS_MS = 1;
const n = (x: number): string => x.toLocaleString('vi-VN');

export type SourcePhase = 'proposed' | 'ready' | 'stale' | 'failed' | 'applied' | 'restored' | 'dismissed' | 'running';
export const UNDO_CHANGED_TEXT_SOURCE = 'Đã có thao tác khác sau thao tác này — khôi phục từ mục Phiên bản chương';
export const STALE_TEXT_SOURCE = 'Chương đã đổi sau khi xem thử (áp một phương án khác, sửa tay, nạp thêm…) — xem thử lại để thấy tác động mới';
export const UNDO_HINT_SOURCE = 'Chương quay về trước thao tác này; bản vừa gỡ vẫn còn ở mục Phiên bản chương.';
/** F22 — chữ nói thẳng khi tải lại ra đúng nội dung đang có (cùng ý với BE `NO_CHANGE_TEXT`). */
export const NO_CHANGE_TEXT_SOURCE = 'Tải lại ra đúng nội dung đang có — không cần áp';
export const RELOAD_PREVIEW_HINT = 'có tải trang nguồn';
/** Chữ khi đang chờ lượt có xem thử đồng bộ (phương án tải lại có thể vài chục giây). */
export const SOURCE_PREVIEW_WAIT_TEXT = 'Đang xem thử (có tải trang nguồn) — có thể mất vài chục giây…';
/** Chữ ở ô soạn khi đang chờ lượt chặng ① — FE chưa biết trước AI có đưa lệnh tải lại không (F4). */
export const SOURCE_SEND_WAIT_TEXT = 'Đang chờ AI trả lời… (nếu có lệnh tải lại trang nguồn có thể mất vài chục giây)';

export const sourceDiffOf = (block: Pick<AssistantBlock, 'diff'>): SourceBlockDiff | null => {
  const d = block.diff as unknown as Partial<SourceBlockDiff> | null;
  if (!d || typeof d !== 'object' || d.kind !== 'source' || !d.op || !d.impact) return null;
  return d as SourceBlockDiff;
};

export interface SourceReloadView { lengthLine: string; head: string; tail: string; warning: string; problems: string[] }

/** F26 — một chương trong thẻ "Cắt rác". */
export interface SourceTrimRow { key: string; orderIndex: number; label: string; lengthLine: string; headCut: string; tailCut: string; reasons: string[] }
export interface SourceTrimView { totalLine: string; rows: SourceTrimRow[]; skippedLine: string; /** nhiều chương ⇒ mặc định thu gọn */ collapsed: boolean }
export const TRIM_COLLAPSE_FROM = 5;
export const NO_TRIM_TEXT_SOURCE = 'Không còn rác đầu/cuối để cắt — không cần áp';

export interface SourceCardView {
  /** F26 — tiêu đề người đọc thay cho `block.title` của BE (chỉ lệnh `trim`); null ⇒ dùng `block.title`. */
  title: string | null;
  phase: SourcePhase;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  headline: string;
  /** Dòng lệnh cụ thể ("Tách chương 4 tại vị trí 1.230", "Gộp chương 4 với chương 5"…). */
  opLine: string;
  detail: string;
  /** "Quyển k cần quét lại · …" — trước khi áp (diff) hoặc sau khi áp (appliedResult). */
  impact: string[];
  reload: SourceReloadView | null;
  /** F26 — kế hoạch cắt rác (lệnh `trim`); null nếu không phải trim / BE chưa trả `diff.trim`. */
  trim: SourceTrimView | null;
  /** F22 — READY nhưng nội dung tải về y hệt: ẩn nút Áp, vẫn cho Bỏ. */
  noChange: boolean;
  canApply: boolean;
  canPreview: boolean;
  previewLabel: string;
  canUndo: boolean;
  undoReason: string | null;
  undoHint: string;
  canDismiss: boolean;
}

export const sourceOpLine = (op: SourceBlockDiff['op'] | undefined, d: Partial<SourceBlockDiff> | null, cmd: AssistantBlock['command']): string => {
  const oi = d?.orderIndex ?? cmd?.orderIndex;
  if (op === 'reload') return `Tải lại chương ${oi ?? '?'} từ link nguồn${d?.reload ? ` — ${n(d.reload.oldChars)} → ${n(d.reload.newChars)} ký tự` : ''}`;
  if (op === 'split') return `Tách chương ${oi ?? '?'} tại vị trí ${n(d?.offset ?? cmd?.offset ?? 0)}${typeof d?.chapterChars === 'number' ? ` / ${n(d.chapterChars)} ký tự` : ''}`;
  if (op === 'mergeNext') return `Gộp chương ${oi ?? '?'} với ${d?.next ? `chương ${d.next.orderIndex}` : 'chương kế sau'}`;
  if (op === 'delete') return `Xoá chương ${oi ?? '?'}`;
  if (op === 'trim') return d?.trim?.scope === 'all' || cmd?.scope === 'all' ? `Cắt rác đầu/cuối ${d?.trim ? d.trim.chapterCount : '?'} chương` : `Cắt rác đầu/cuối chương ${oi ?? '?'}`;
  return '';
};

const reloadViewOf = (d: SourceBlockDiff): SourceReloadView | null => {
  const r = d.reload;
  if (!r) return null;
  return {
    lengthLine: `Độ dài ${n(r.oldChars)} → ${n(r.newChars)} ký tự`, head: str(r.head), tail: str(r.tail), warning: reloadWarningText(r),
    problems: r.ok === false ? (r.reasons ?? []).map(str).filter(Boolean) : [],
  };
};

const diffN = (a: number, b: number): string => `${n(a)} → ${n(b)} (${b - a >= 0 ? '+' : '−'}${n(Math.abs(b - a))})`;

const trimViewOf = (t: SourceTrimDiff | undefined): SourceTrimView | null => {
  if (!t) return null;
  const rows: SourceTrimRow[] = (t.chapters ?? []).map((c) => ({
    key: c.chapterId, orderIndex: c.orderIndex, label: `Chương ${c.orderIndex}${str(c.name) ? ` — ${str(c.name)}` : ''}`, lengthLine: diffN(c.before, c.after),
    headCut: str(c.headCut), tailCut: str(c.tailCut),
    reasons: (c.reasons ?? []).map((r) => `${r.side === 'head' ? 'Đầu' : 'Cuối'} chương: ${str(r.text)}`),
  }));
  const locked = [...(t.skippedLocked ?? [])].sort((a, b) => a - b);
  return {
    totalLine: `Tổng ${n(t.totalBefore)} → ${n(t.totalAfter)} ký tự (${t.totalAfter - t.totalBefore >= 0 ? '+' : '−'}${n(Math.abs(t.totalAfter - t.totalBefore))})`,
    rows, skippedLine: locked.length ? `Bỏ qua chương ${locked.join(', ')} — đã viết/đã đẩy nên khoá, không cắt được` : '', collapsed: rows.length >= TRIM_COLLAPSE_FROM,
  };
};

const undoState = (block: AssistantBlock, headModifiedAt: string | null | undefined, busy: boolean, busyReason?: string | null): { canUndo: boolean; undoReason: string | null } => {
  if (busy) return { canUndo: false, undoReason: busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.' };
  if (!str(block.appliedResult?.versionId)) return { canUndo: false, undoReason: 'Khối này không có phiên bản để khôi phục — dùng mục Phiên bản chương' };
  const applied = Date.parse(str(block.appliedResult?.modifiedAt));
  const head = Date.parse(str(headModifiedAt));
  if (Number.isNaN(head)) return { canUndo: false, undoReason: 'Đang tải dữ liệu chương hiện tại…' };
  if (Number.isNaN(applied) || Math.abs(head - applied) > UNDO_EPS_MS) return { canUndo: false, undoReason: UNDO_CHANGED_TEXT_SOURCE };
  return { canUndo: true, undoReason: null };
};

/** `busy` ⇒ mọi nút GHI (Xem thử, Áp, Khôi phục) tắt; "Bỏ" không bị khoá. Khối ① hiện KHÔNG bao giờ PROPOSED (xem thử đồng bộ chạy ngay), nhưng vẫn xử lý. */
export const sourceCardView = (block: AssistantBlock, busy: boolean, headModifiedAt?: string | null, busyReason?: string | null): SourceCardView => {
  const d = sourceDiffOf(block);
  const op = (d?.op ?? block.command?.op) as SourceBlockDiff['op'] | undefined;
  const reloadOp = op === 'reload';
  const retryLabel = reloadOp ? `Xem thử lại (${RELOAD_PREVIEW_HINT})` : 'Xem thử lại';
  const rationale = str(block.command?.rationale);
  const trimTitle = op === 'trim' ? (d?.trim?.scope === 'all' || block.command?.scope === 'all' ? `Cắt rác — ${d?.trim ? d.trim.chapterCount : '?'} chương` : `Cắt rác — chương ${d?.orderIndex ?? block.command?.orderIndex ?? '?'}`) : null;
  const base = {
    title: trimTitle, trim: d?.op === 'trim' || op === 'trim' ? trimViewOf(d?.trim) : null, noChange: false, opLine: sourceOpLine(op, d, block.command), impact: impactLines(d?.impact), reload: d ? reloadViewOf(d) : null,
    canApply: false, canPreview: false, previewLabel: retryLabel, canUndo: false, undoReason: null as string | null, undoHint: UNDO_HINT_SOURCE, canDismiss: false,
    detail: rationale ? `Lý do: ${rationale}` : '',
  };
  switch (block.state) {
    case 'PROPOSED':
      return { ...base, phase: 'proposed', tone: 'neutral', headline: 'Chưa xem thử', canPreview: !busy, previewLabel: reloadOp ? `Xem thử (${RELOAD_PREVIEW_HINT})` : 'Xem thử', canDismiss: true };
    case 'PREVIEW_RUNNING':
      return { ...base, phase: 'running', tone: 'neutral', headline: reloadOp ? SOURCE_PREVIEW_WAIT_TEXT : 'Đang xem thử…', canDismiss: true };   // 261015: BE cho DISMISS từ PREVIEW_RUNNING
    case 'FAILED':
      return { ...base, phase: 'failed', tone: 'danger', headline: 'Xem thử lỗi', detail: friendlyJobNote(str(block.error)) || base.detail, canPreview: !busy, canDismiss: true };
    case 'STALE':
      return { ...base, phase: 'stale', tone: 'warn', headline: 'Xem thử đã cũ', detail: STALE_TEXT_SOURCE, canPreview: !busy, canDismiss: true };
    case 'READY': {
      // F22: cờ do BE đặt lúc xem thử; chỉ có nghĩa khi khối còn READY (đã áp / đã gỡ thì không đụng).
      if (d && (d.noChange === true || d.reload?.unchanged === true)) {
        const trimOp = op === 'trim';
        return {
          ...base, phase: 'ready', tone: 'neutral', headline: trimOp ? 'Không có gì để cắt' : 'Không có gì để áp',
          detail: [trimOp ? NO_TRIM_TEXT_SOURCE : NO_CHANGE_TEXT_SOURCE, base.detail].filter(Boolean).join('. '), impact: [], trim: null, noChange: true, canApply: false, canDismiss: true,
        };
      }
      const warn = !!d && (d.impact.volumesStale.length > 0 || d.impact.episodesTouched.length > 0 || !!d.reload?.warning || d.reload?.ok === false);
      return { ...base, phase: 'ready', tone: warn ? 'warn' : 'ok', headline: 'Sẵn sàng áp', canApply: !busy && !!d, canDismiss: true };
    }
    case 'APPLIED': {
      const v = block.appliedResult?.versionNo;
      return {
        ...base, phase: 'applied', tone: 'ok', headline: typeof v === 'number' ? `Đã áp (v${v})` : 'Đã áp', detail: 'Hoàn tác được ngay ở đây hoặc ở mục Phiên bản chương (chỉ thao tác mới nhất).',
        impact: impactLines(block.appliedResult?.sourceImpact ?? d?.impact), ...undoState(block, headModifiedAt, busy, busyReason),
      };
    }
    case 'RESTORED': {
      const rv = block.appliedResult?.restoreVersionNo;
      return {
        ...base, phase: 'restored', tone: 'neutral', headline: typeof rv === 'number' ? `Đã gỡ (v${rv})` : 'Đã gỡ',
        detail: 'Chương đã quay về bản trước. Muốn làm lại thì bấm "Xem thử lại" rồi Áp.', impact: [], reload: null, canPreview: !busy, canDismiss: true,
      };
    }
    default:
      return { ...base, phase: 'dismissed', tone: 'neutral', headline: 'Đã bỏ', detail: '' };
  }
};
